import { useCallback, useEffect, useState } from 'react'

/**
 * Tracks whether an element's rendered width is below `minWidth`. Returns a callback ref (so the
 * element can mount late, e.g. after a loading state) and the current answer. Reports `false`
 * until the element has been measured, and in environments without `ResizeObserver`.
 */
export function useIsNarrowerThan<T extends HTMLElement>(minWidth: number): [(element: T | null) => void, boolean] {
  const [element, setElement] = useState<T | null>(null)
  const [isNarrower, setIsNarrower] = useState(false)

  const ref = useCallback((node: T | null) => setElement(node), [])

  useEffect(() => {
    if (!element || typeof ResizeObserver === 'undefined') {
      setIsNarrower(false)
      return
    }
    const measure = () => setIsNarrower(element.clientWidth > 0 && element.clientWidth < minWidth)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [element, minWidth])

  return [ref, isNarrower]
}
